import type { ReactNode } from 'react';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import SpotlightTarget from '@atlaskit/onboarding/spotlight-target';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { TopLevelSpacer } from '@atlaskit/side-nav-items/top-level-spacer';

import { ConfluenceWorkspaceAri, IdentityUserAri } from '@atlassian/ari';
import type {
	GlobalShortcutLinkItem,
	ProductLink,
	fnGetFilteredProductLinks,
} from '@atlassian/global-shortcuts/types';
import { RequestStatus } from '@atlassian/global-shortcuts/constants';
import { useJira } from '@atlassian/global-shortcuts/jira';
import { useLoom } from '@atlassian/global-shortcuts/loom';
import { InvitedAppShortcutsContainer } from '@atlassian/cross-join-nudges/invited-app-shortcuts/invited-app-shortcuts-container/main';
import { ShortcutsMenu } from '@atlassian/cross-join-nudges/invited-app-shortcuts/shortcuts-menu/main';
import type { ShortcutItemsConfig } from '@atlassian/cross-join-nudges/types';
import type { ShortcutsConfig } from '@atlassian/global-side-navigation/common/types';

import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import {
	useRouteActions,
	useRouteDataRef,
	useTransitionId,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import { LoadableLazy } from '@confluence/loadable';
import { fg } from '@confluence/feature-gating';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { useFlyoutActions } from '@confluence/flyout-store';
import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { ConnectorPromoSidebarEntry } from '@confluence/experiment-twcg-1489-cc-3p-connector-onboarding/entry-points/ConnectorPromoSidebarEntry.ts';
import { TwoStageHydration } from '@confluence/ssr-utilities/entry-points/TwoStageHydration.ts';

import { CompanyHubItem } from '../PlatformNavigation/AppShortcuts/apps/CompanyHubItem/CompanyHubItem.tsx';
import type { MenuId } from '../PlatformNavigation/PlatformNavigationTypes.ts';

import { usePlatformCustomizationsWithSimplifyNav } from './usePlatformCustomizationsWithSimplifyNav.ts';
import { GLOBAL_NAVIGATION_METRIC } from './perf.config.ts';
import { useMenuItems } from './useMenuItems.ts';
import { MoreMenuItem } from './MoreMenuItem.tsx';
import { GlobalAppShortcutsSection } from './GlobalAppShortcuts/GlobalAppShortcutsSection.tsx';
import { CustomHomeItem } from './GlobalItems/CustomHomeItem.tsx';
import { ForYouItem } from './GlobalItems/ForYouItem.tsx';
import { RecentItem } from './GlobalItems/RecentItem.tsx';
import { StarredItem } from './GlobalItems/StarredItem.tsx';
import { SpacesItem } from './GlobalItems/SpacesItem.tsx';
import { AppsItem } from './GlobalItems/AppsItem/index.tsx';
import { TopicsItem } from './GlobalItems/TopicsItem.tsx';
import { GoalsItem } from './GlobalItems/GoalsItem.tsx';
import { TeamsItem } from './GlobalItems/TeamsItem.tsx';
import { DraftsItem } from './GlobalItems/DraftsItem.tsx';
import { TasksItem } from './GlobalItems/TasksItem.tsx';
import { TemplatesItem } from './GlobalItems/TemplatesItem.tsx';
import { CustomizeSidebarItem } from './GlobalItems/CustomizeSidebarItem.tsx';
import type {
	GlobalNavigationComponentQuery$data,
	GlobalNavigationComponentQuery$variables,
} from './__generated__/GlobalNavigationComponentQuery.graphql.ts';
import { ReorderShortcutsContainer } from './reorderShortcutsContainer.tsx';
import { useLegacyInvitedAppShortcutsRecommendations } from './LegacyInvitedAppShortcutsRecommendations.tsx';

type SettingsMenuItem = NonNullable<
	NonNullable<
		NonNullable<GlobalNavigationComponentQuery$data['settings_navigationCustomisation']>['sidebar']
	>['nodes']
>[number];

const CustomizationModalLoader = LoadableLazy({
	loader: async () => {
		const { CustomizationModalComponent } = await import(
			/* webpackChunkName: "loadable-CustomizationModal" */ './CustomizeSidebar/CustomizationModal.tsx'
		);
		return CustomizationModalComponent;
	},
});

export const PLATFORM_MENU_ID_PREFIX = 'confluence.sidebar.';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks.ts'))
			.fireMoreMenuClickedAnalytics,
);

const hasAccess = (accessStatus: AccessStatus | null) =>
	![AccessStatus.ANONYMOUS_ACCESS, AccessStatus.UNLICENSED_AUTHENTICATED_ACCESS, null].includes(
		accessStatus,
	);

const hasGlobalAppShortcutsAccess = (accessStatus: AccessStatus | null) =>
	![
		AccessStatus.ANONYMOUS_ACCESS,
		AccessStatus.EXTERNAL_SHARE_ACCESS,
		AccessStatus.EXTERNAL_COLLABORATOR_ACCESS,
		AccessStatus.NOT_PERMITTED,
		null,
	].includes(accessStatus);

type GlobalNavigationComponentProps = {
	productSideNavigation?: ReactNode;
	isOnAnySettingsScreen?: boolean;
	isCollapsed?: boolean;
};

const GlobalItemComponents = {
	customHome: CustomHomeItem,
	yourWork: ForYouItem,
	companyHub: CompanyHubItem,
	recent: RecentItem,
	starred: StarredItem,
	spaces: SpacesItem,
	apps: AppsItem,
	goals: GoalsItem,
	topics: TopicsItem,
	teams: TeamsItem,
	drafts: DraftsItem,
	tasks: TasksItem,
	templates: TemplatesItem,
	customize: CustomizeSidebarItem,
};

export const GlobalNavigationComponent = ({
	productSideNavigation,
	isOnAnySettingsScreen,
	isCollapsed,
}: GlobalNavigationComponentProps): React.JSX.Element => {
	const { accessStatus, cloudId, userId, activationId } = useSessionData();
	const [peekingId, setPeekingId] = useState<MenuId | undefined>(undefined);
	const [isMoreMenuOpen, setIsMoreMenuOpen] = useState<boolean>(false);
	const routeDataRef = useRouteDataRef();
	const { push } = useRouteActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);
	const { openFlyout, closeFlyout } = useFlyoutActions();
	const transitionId = useTransitionId();
	const [getFilteredProductLinks, setGetFilteredProductLinks] = useState<
		fnGetFilteredProductLinks | undefined
	>(undefined);

	const [isCustomizeSidebarDialogVisible, setIsCustomizeSidebarDialogVisible] =
		useState<boolean>(false);

	const jiraShortcut: GlobalShortcutLinkItem = useJira('');
	const loomShortcut: GlobalShortcutLinkItem = useLoom('');

	useEffect(() => {
		if (transitionId && transitionId > 0) {
			//on any transition close open flyout. On initial Load transitionId = 0
			closeFlyout();
		}
	}, [closeFlyout, transitionId]);

	const { data, error } = useQueryInterop<
		GlobalNavigationComponentQuery$data,
		GlobalNavigationComponentQuery$variables
	>(GlobalNavigationComponentQuery, {
		interopApolloAggClient: true,
		variables: {
			entityAri: ConfluenceWorkspaceAri.create({ siteId: cloudId, activationId }).toString(),
			ownerAri: userId ? IdentityUserAri.create({ userId }).toString() : '',
		},
		skip: !userId || !activationId,
	});

	const platformCustomizations = useMemo(() => {
		if (data?.settings_navigationCustomisation?.sidebar?.nodes) {
			return data.settings_navigationCustomisation.sidebar.nodes
				.filter((node): node is NonNullable<typeof node> => node != null)
				.map((node) => ({
					menuId: node.menuId?.replace(PLATFORM_MENU_ID_PREFIX, '') as MenuId,
					visible: Boolean(node.visible),
				}));
		}
		return [];
	}, [data]);

	const { platformCustomizationsWithSimplifyNav } =
		usePlatformCustomizationsWithSimplifyNav(platformCustomizations);

	//only show customization and fetch data if we meet these conditions
	const showCustomization = !!activationId && hasAccess(accessStatus);

	// only render global app shortcuts if we meet these conditions
	const showGlobalAppShortcuts = hasGlobalAppShortcutsAccess(accessStatus);

	// Two separate flags to keep exposure balanced:
	//
	// * `isInvitedAppShortcutsEligible` — the kill-switch gate + prerequisites (cloudId/userId/GAS
	//   access). This is the whole cohort: BOTH experiment arms. It drives the GRS read + exposure
	//   (below) so treatment and control are exposed at the same divergence point ("has a rec").
	// * `isInvitedAppShortcutsTreatment` — eligible AND the experiment resolves to the treatment arm.
	//   Read with `UNSAFE_expValEqualsNoExposure` (no exposure — the real exposure is fired inside the
	//   hook, tagged by `expVal`). This drives only the RENDER (show the decorated GRS nudge + hand the
	//   Jira shortcut over from GlobalAppShortcuts); control renders the plain menu.
	const isInvitedAppShortcutsEligible =
		showGlobalAppShortcuts &&
		!!cloudId &&
		!!userId &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		fg('twcg-460-preload-grs-invited-app-shortcuts-gate');
	const isInvitedAppShortcutsTreatment =
		isInvitedAppShortcutsEligible &&
		UNSAFE_expValEqualsNoExposure('twcg-460-preload-grs-invited-app-shortcuts', 'isEnabled', true);

	// Read the GRS recommendations for the WHOLE gated cohort — both experiment arms — not just the
	// treatment arm. This keeps the exposure balanced: the hook fires the `twcg-460-preload-grs-…`
	// exposure for treatment and control alike (tagged by `expVal`) whenever there is a recommendation
	// to show, so both arms enter the analysis. Only the render (below) is treatment-gated. Gating the
	// read on `isInvitedAppShortcutsTreatment` instead would exclude control from exposure entirely.
	// `recommendedApps` also drives whether the decorated nudge renders and whether the Jira shortcut
	// is handed over from the main GlobalAppShortcuts section — an empty result keeps Jira in
	// GlobalAppShortcuts instead of hiding it.
	const { recommendedApps, resolverResult } = useLegacyInvitedAppShortcutsRecommendations({
		cloudId: cloudId ?? '',
		userId: userId ?? '',
		enabled: isInvitedAppShortcutsEligible,
	});
	const hasInvitedAppRecommendation = isInvitedAppShortcutsTreatment && recommendedApps.length > 0;

	const { globalMenuItems, moreMenuItems, appShortcutMenuItems, allMenuItems } = useMenuItems(
		showCustomization,
		accessStatus,
		platformCustomizationsWithSimplifyNav,
		getFilteredProductLinks,
	);

	const moreMenuOnClickCallback = useCallback(
		(menuId: MenuId) => {
			setIsMoreMenuOpen(!isMoreMenuOpen);

			// TODO: it would be great if we could refactor the following menu-specific logic into the respective menu components
			if (menuId === 'customize') {
				setIsCustomizeSidebarDialogVisible(true);
				void fireClickAnalytics('customiseSidebar');
			} else {
				void fireClickAnalytics(menuId);
			}

			if (menuId === 'recent' || menuId === 'starred' || menuId === 'spaces') {
				openFlyout(menuId);
				setPeekingId(menuId);
			}

			if (menuId === 'apps') {
				push('/wiki/marketplace/discover');
			}
		},
		[fireClickAnalytics, isMoreMenuOpen, openFlyout, push],
	);

	// The invited-app treatment takes over each recommended shortcut independently. The normal
	// GlobalAppShortcuts section strips only products that are actively rendered above the fold, so
	// neither Jira nor Loom can disappear or render twice.
	const shouldHandOverJiraShortcut =
		hasInvitedAppRecommendation && recommendedApps.some(({ product }) => product === 'jira');
	const shouldHandOverLoomShortcut =
		hasInvitedAppRecommendation && recommendedApps.some(({ product }) => product === 'loom');

	const invitedAppShortcutsConfig: ShortcutItemsConfig = useMemo(() => {
		// The container gates actual visibility on the GRS recommendations; the config only needs to
		// carry the shortcut so its product links resolve.
		if (!isInvitedAppShortcutsTreatment) {
			return {};
		}
		return recommendedApps.reduce<ShortcutItemsConfig>((config, { product }) => {
			if (product === 'jira') {
				return { ...config, jira: jiraShortcut };
			}
			if (product === 'loom') {
				return { ...config, loom: loomShortcut };
			}
			return config;
		}, {});
	}, [isInvitedAppShortcutsTreatment, jiraShortcut, loomShortcut, recommendedApps]);

	// Resolve the invited-app shortcut's href from the BFF filter that GlobalAppShortcuts hands us.
	// `ShortcutsMenu` renders a shortcut with no resolved link as a URL-less (dead) item, so this has
	// to be a live read: until the filter reports COMPLETE we pass nothing and the menu renders the
	// shortcut unlinked, then re-renders with the href once the links land.
	const invitedAppProductLinks = useMemo<Record<string, ProductLink[]>>(() => {
		if (!getFilteredProductLinks) {
			return {};
		}
		return Object.values(invitedAppShortcutsConfig).reduce<Record<string, ProductLink[]>>(
			(acc, config) => {
				if (config.type === 'link' && config.apsKey) {
					const { links, status } = getFilteredProductLinks(config.apsKey);
					if (status === RequestStatus.COMPLETE) {
						acc[config.apsKey] = links;
					}
				}
				return acc;
			},
			{},
		);
	}, [getFilteredProductLinks, invitedAppShortcutsConfig]);

	// The item "Customize sidebar" when it's one of the visible global items and app shortcuts:
	let globalCustomizeMenuItem: ReactNode = null;

	const moreMenuItem = (
		<MoreMenuItem
			moreMenuItems={moreMenuItems}
			isOpen={isMoreMenuOpen}
			onClick={moreMenuOnClickCallback}
			toggleMenu={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
			peekingId={peekingId}
			isCollapsed={isCollapsed}
		/>
	);

	// Leaf renderer for the invited-app-shortcuts menu: order the decorated shortcuts per the user's
	// customisation, then render them with the resolved product links.
	const renderInvitedAppShortcutsMenu = (referrableShortcuts: ShortcutsConfig) => (
		<ReorderShortcutsContainer
			shortcutsConfig={referrableShortcuts}
			customizationMenuConfig={platformCustomizationsWithSimplifyNav}
		>
			{({ shortcuts }) => (
				<ShortcutsMenu
					shortcutItems={shortcuts}
					product="confluence"
					invitedAppProductLinks={invitedAppProductLinks}
				/>
			)}
		</ReorderShortcutsContainer>
	);

	return (
		<>
			<MenuList>
				{!isOnAnySettingsScreen && (
					<SpotlightTarget name="enhance-orientation-spotlight-tour-target-3">
						<div data-vc="nav4-globalItems">
							{globalMenuItems.map(({ menuId }) => {
								const Component = GlobalItemComponents[menuId];
								let node: ReactNode = null;
								if (Component) {
									const baseNode = (
										<Component
											key={menuId}
											setIsCustomizeSidebarDialogVisible={setIsCustomizeSidebarDialogVisible}
										/>
									);
									node = baseNode;
									if (menuId === 'companyHub') {
										// companyHub is always rendered via GlobalAppShortcutsSection —
										// suppress it here to prevent a double-render.
										return null;
									} else if (menuId === 'customize') {
										// The item "Customize sidebar" should be:
										// - after the app shortcuts if any;
										// - visually divided from the preceeding global items or app shortcuts.
										globalCustomizeMenuItem = node;
										return null;
									}
								}
								return node;
							})}
							<TwoStageHydration name="invited-app-shortcuts-container">
								{/*
								 * Render the decorated invited-app nudge ONLY when GRS actually recommends an app. On an
								 * empty result render nothing here — Jira and Loom stay in the main
								 * GlobalAppShortcuts section (each is only handed over while recommended), so neither
								 * is hidden. The decorated treatment (label <div> injected into
								 * the menu item's <span>, hover popup) is a data-driven client enhancement whose
								 * SSR/first-client render can diverge under streaming SSR (React #418/#422), so it
								 * stays inside TwoStageHydration.
								 */}
								{hasInvitedAppRecommendation && cloudId && userId && (
									<InvitedAppShortcutsContainer
										cloudId={cloudId}
										userId={userId}
										recommendedApps={recommendedApps}
										sourceApp="confluence"
										shortcutsConfig={invitedAppShortcutsConfig}
										resolverResult={resolverResult}
									>
										{({ referrableShortcuts }) =>
											renderInvitedAppShortcutsMenu(referrableShortcuts)
										}
									</InvitedAppShortcutsContainer>
								)}
							</TwoStageHydration>
							{moreMenuItems.map((result) => {
								const Component = GlobalItemComponents[result.menuId];
								return (
									Component && (
										<Component
											key={result.menuId}
											isHidden
											peekingId={peekingId}
											setPeekingId={setPeekingId}
											setIsCustomizeSidebarDialogVisible={setIsCustomizeSidebarDialogVisible}
										/>
									)
								);
							})}
							{/* The app shortcuts section is after and visually divided from the global items section:  */}
							{/* DEPRECATED: App Shortcuts is now being managed by Global App Shortcuts above  */}
							{/* The item "Customize sidebar" is after and visually divided from the global items and/or app shortcuts sections: */}
							{globalCustomizeMenuItem && (
								<>
									<TopLevelSpacer />
									{globalCustomizeMenuItem}
								</>
							)}
							{error && <ErrorDisplay error={error} />}
							{isCustomizeSidebarDialogVisible && (
								<CustomizationModalLoader
									onClose={() => setIsCustomizeSidebarDialogVisible(false)}
									productItemsData={allMenuItems}
									appItemsData={appShortcutMenuItems}
									navigationCustomisationId={data?.settings_navigationCustomisation?.id || ''}
								/>
							)}
							<PageSegmentLoadEnd metric={GLOBAL_NAVIGATION_METRIC} />
						</div>
					</SpotlightTarget>
				)}
				<TopLevelSpacer />
			</MenuList>

			{productSideNavigation}

			{/* Global App Shortcuts: Platform Component for supporting Global App Shortcuts */}
			{!isOnAnySettingsScreen && showGlobalAppShortcuts && (
				<MenuList>
					<TopLevelSpacer />
					<GlobalAppShortcutsSection
						orderedMenuConfig={platformCustomizationsWithSimplifyNav}
						onUpdateGetFilteredProductLinks={(fn: fnGetFilteredProductLinks) => {
							setGetFilteredProductLinks(() => fn);
						}}
						shouldHandOverJiraShortcut={shouldHandOverJiraShortcut}
						shouldHandOverLoomShortcut={shouldHandOverLoomShortcut}
					/>
					{moreMenuItem}
					<ConnectorPromoSidebarEntry />
				</MenuList>
			)}
		</>
	);
};

export const updateNavigationCustomization = (
	variables: GlobalNavigationComponentQuery$variables,
	nodes: SettingsMenuItem[],
	id: string,
): void => {
	writeQueryInterop({
		query: GlobalNavigationComponentQuery,
		variables,
		data: {
			settings_navigationCustomisation: {
				__typename: 'SettingsNavigationCustomisation',
				id,
				sidebar: {
					__typename: 'SettingsMenuItemConnection',
					nodes,
				},
			},
		},
	});
};

const GlobalNavigationComponentQuery = graphql`
	query GlobalNavigationComponentQuery($entityAri: ID!, $ownerAri: ID!) {
		settings_navigationCustomisation(entityAri: $entityAri, ownerAri: $ownerAri) {
			id
			sidebar {
				nodes {
					menuId
					visible
				}
			}
		}
	}
`;
